import React, { useState, useEffect } from 'react';
import { 
  Search, 
  HelpCircle, 
  CheckSquare, 
  Bell, 
  LogOut,
  Building,
  Home,
  AlertTriangle,
  Eye,
  EyeOff,
  MoreHorizontal,
  ChevronDown,
  ChevronUp,
  Filter,
  Download,
  Upload,
  Settings,
  User,
  Menu,
  X,
  ChevronRight,
  ChevronLeft,
  ExternalLink,
  Copy,
  Share,
  Heart,
  Bookmark,
  Flag,
  Tag,
  Hash,
  AtSign,
  Calendar,
  Clock,
  MapPin,
  Phone,
  Mail,
  MessageSquare,
  AlertCircle,
  Users,
  BarChart3,
  TrendingUp,
  Star,
  DollarSign,
  Target,
  Activity,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Edit,
  Trash2,
  Plus
} from 'lucide-react';
import { findDuplicateProperties, findDuplicateContacts } from '../../api/propertyManagement';
import toast from 'react-hot-toast';

const DuplicateCheck = () => {
  const [activeTab, setActiveTab] = useState('duplicate-check');
  const [loading, setLoading] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [contactDuplicateGroups, setContactDuplicateGroups] = useState([]);
  const [searchType, setSearchType] = useState('properties');

  // Fetch duplicate data on mount
  useEffect(() => {
    if (activeTab === 'duplicate-check') {
      fetchDuplicates();
    }
  }, [activeTab, searchType]);

  const fetchDuplicates = async () => {
    try {
      setLoading(true);
      if (searchType === 'properties') {
        const response = await findDuplicateProperties();
        setDuplicateGroups(response.data.duplicates || []);
      } else {
        const response = await findDuplicateContacts();
        setContactDuplicateGroups(response.data.duplicates || []);
      }
    } catch (error) {
      console.error('Error fetching duplicates:', error);
      // toast.error('Failed to load duplicates');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const tabs = [
    { id: 'duplicate-check', label: 'Duplicate Check' },
    { id: 'recently-merged', label: 'Recently Merged' },
    { id: 'view-ignore-list', label: 'View Ignore List' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Top Header Bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search properties or contacts"
                className="pl-10 pr-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent bg-white w-80"
              />
            </div>
          </div>
       
        </div>
        <div className="flex items-center space-x-4 mt-3">
          <button 
            onClick={() => setSearchType('properties')}
            className={`px-4 py-2 rounded-md text-sm ${
              searchType === 'properties' 
                ? 'bg-blue-600 text-white hover:bg-blue-700' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Q Property
          </button>
          <button 
            onClick={() => setSearchType('contacts')}
            className={`px-4 py-2 rounded-md text-sm ${
              searchType === 'contacts' 
                ? 'bg-emerald-600 text-white hover:bg-emerald-700' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Q Contact
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
     

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-slate-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-700'
                      : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Results Section */}
        <div className="bg-white rounded-lg shadow-md border border-slate-300">
          <div className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Results</h2>
                <p className="text-sm text-slate-600 mt-1">
                  {loading ? 'Loading...' : searchType === 'properties' 
                    ? `Found ${duplicateGroups.length} duplicate group(s)` 
                    : `Found ${contactDuplicateGroups.length} duplicate group(s)`}
                </p>
              </div>
              <button 
                onClick={fetchDuplicates}
                disabled={loading}
                className="px-4 py-2 bg-[#55C5D0] text-white rounded-md hover:bg-[#3bb3be] text-sm disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55C5D0] mx-auto"></div>
                  <p className="mt-4 text-slate-600">Loading duplicates...</p>
                </div>
              ) : searchType === 'properties' && duplicateGroups.length > 0 ? (
                duplicateGroups.map((group, groupIndex) => (
                  <div key={groupIndex} className="mb-8">
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-2">
                      <div className="flex items-center">
                        <AlertTriangle className="w-5 h-5 text-amber-500 mr-2" />
                        <div>
                          <p className="font-semibold text-amber-900">
                            Duplicate Group {groupIndex + 1} - {group.match_type.replace('_', ' ').toUpperCase()}
                          </p>
                          <p className="text-sm text-amber-700">
                            Match on: {group.match_field} = "{group.match_value}" ({group.count} properties)
                          </p>
                        </div>
                      </div>
                    </div>
                    <table className="min-w-full divide-y divide-slate-200 mb-4">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Property Address
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Created
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Modified
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {group.properties.map((property, propIndex) => (
                          <tr key={property.id} className={`hover:bg-slate-50 ${propIndex > 0 ? 'bg-red-50' : ''}`}>
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <Home className="w-4 h-4 text-slate-400 mr-2" />
                                <span className={`text-sm font-medium ${propIndex > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                                  {property.full_address || property.title || 'N/A'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                              {property.id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                              {formatDate(property.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                              {formatDate(property.updated_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                              {property.status?.name || 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              ) : searchType === 'contacts' && contactDuplicateGroups.length > 0 ? (
                contactDuplicateGroups.map((group, groupIndex) => (
                  <div key={groupIndex} className="mb-8">
                    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-2">
                      <div className="flex items-center">
                        <AlertTriangle className="w-5 h-5 text-amber-500 mr-2" />
                        <div>
                          <p className="font-semibold text-amber-900">
                            Duplicate Group {groupIndex + 1} - {group.match_type.replace('_', ' ').toUpperCase()}
                          </p>
                          <p className="text-sm text-amber-700">
                            Match on: {group.match_field} = "{group.match_value}" ({group.count} contacts)
                          </p>
                        </div>
                      </div>
                    </div>
                    <table className="min-w-full divide-y divide-slate-200 mb-4">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Contact Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Phone
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                            Created
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {group.contacts.map((contact, contactIndex) => (
                          <tr key={contact.id} className={`hover:bg-slate-50 ${contactIndex > 0 ? 'bg-red-50' : ''}`}>
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <User className="w-4 h-4 text-slate-400 mr-2" />
                                <span className={`text-sm font-medium ${contactIndex > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                                  {contact.name || 'N/A'}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                              {contact.id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                              {contact.email || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                              {contact.phone_number || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                              {formatDate(contact.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <Home className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    No Duplicates Found
                  </h3>
                  <p className="text-slate-600">
                    {searchType === 'properties' 
                      ? 'No duplicate properties were detected in the system.' 
                      : 'No duplicate contacts were detected in the system.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

     
    </div>
  );
};

export default DuplicateCheck;
